import { Link } from "react-router-dom";
import { downloadAgreementPdf } from "../lib/downloadPdf";
import { useAuth } from "../context/AuthContext";
import { isoToEU } from "../lib/format";
import { useSafeFetch } from "../lib/useSafeFetch";
import { FileDown, Award, Share2, Copy } from "lucide-react";
import { toast } from "sonner";

const STATUS_BADGE = {
  confirmed: { label: "Confirmed", bg: "#004B35", color: "#fff" },
  pending_payment: { label: "Pending Payment", bg: "#F1ECDA", color: "#7C5E00" },
  pending: { label: "Booking Request", bg: "#E0F2FE", color: "#075985" },
  cancelled: { label: "Cancelled", bg: "#FEE2E2", color: "#991B1B" },
  completed: { label: "Completed", bg: "#0A0A0A", color: "#fff" },
};

const balanceDue = (b) => {
  const total = Number(b.total || 0);
  const paid = Number(b.paid_amount || 0);
  return Math.max(total - paid, 0);
};

export default function Dashboard() {
  const { user } = useAuth();
  const { data: bookings, loading } = useSafeFetch("/bookings/me", {
    defaultValue: [], asList: true,
  });
  const { data: loyalty } = useSafeFetch("/loyalty/me", { defaultValue: null });
  const { data: referral } = useSafeFetch("/referrals/me", { defaultValue: null });
  const safeBookings = bookings;

  return (
    <div className="px-6 md:px-12 lg:px-20 py-12" data-testid="dashboard-page">
      <p className="overline mb-3">Hello, {user?.name}</p>
      <h1 className="font-display text-5xl md:text-6xl tracking-tight">
        Your <em className="italic">journeys</em>.
      </h1>
      <p className="mt-3 text-neutral-600">All your reservations in one place.</p>

      {loyalty && loyalty.enabled && (
        <LoyaltyCard loyalty={loyalty} />
      )}
      {referral && <ReferralCard referral={referral} />}

      <div className="divider-line my-8" />

      {loading && <p className="text-neutral-500">Loading…</p>}
      {!loading && safeBookings.length === 0 && (
        <div className="border border-dashed border-black/15 p-12 text-center">
          <p className="font-display text-2xl mb-3">No bookings yet</p>
          <p className="text-neutral-600 mb-6">Start by exploring our curated fleet.</p>
          <Link to="/vehicles" className="bg-black text-white px-6 py-3 text-sm hover:bg-neutral-800 transition-colors">Browse Fleet</Link>
        </div>
      )}
      {!loading && safeBookings.length > 0 && (
        <div className="space-y-6">
          {safeBookings.map((b) => {
            const badge = STATUS_BADGE[b.status] || STATUS_BADGE.pending_payment;
            return (
              <div key={b.id} className="bg-white border border-black/10 p-6 md:p-8" data-testid={`booking-row-${b.id}`}>
                <div className="grid md:grid-cols-12 gap-6 items-center">
                  <div className="md:col-span-2">
                    <div className="bg-[#EFECE6] overflow-hidden" style={{ aspectRatio: "4/3" }}>
                      <img src={b.vehicle_snapshot?.image_url} alt={b.vehicle_snapshot?.name} className="w-full h-full object-cover" />
                    </div>
                  </div>
                  <div className="md:col-span-5">
                    <p className="overline">{b.vehicle_snapshot?.brand}</p>
                    <p className="font-display text-2xl mt-1">{b.vehicle_snapshot?.name}</p>
                    <p className="text-xs text-neutral-500 mt-2">
                      {isoToEU(b.pickup_date)} → {isoToEU(b.return_date)} · {b.days} day{b.days > 1 ? "s" : ""}
                    </p>
                    <p className="text-xs text-neutral-500 mt-1">
                      {b.pickup_location} → {b.return_location}
                    </p>
                  </div>
                  <div className="md:col-span-2">
                    <p className="overline mb-1">Status</p>
                    <span
                      className="inline-block text-xs px-3 py-1 uppercase tracking-wider"
                      style={{ background: badge.bg, color: badge.color }}
                      data-testid={`booking-status-${b.id}`}
                    >
                      {badge.label}
                    </span>
                  </div>
                  <div className="md:col-span-2 text-right">
                    <p className="overline mb-1">Total</p>
                    <p className="font-display text-2xl">€{b.total.toFixed(2)}</p>
                    {b.payment_status === "partial" && balanceDue(b) > 0 && (
                      <p
                        className="text-[11px] text-amber-700 mt-1"
                        data-testid={`balance-due-${b.id}`}
                      >
                        Balance €{balanceDue(b).toFixed(2)} due at pickup
                      </p>
                    )}
                  </div>
                  <div className="md:col-span-1 text-right">
                    {b.payment_status !== "paid" && b.payment_status !== "partial" ? (
                      <Link
                        to={`/booking/${b.id}`}
                        className="text-xs underline"
                        data-testid={`pay-booking-${b.id}`}
                      >
                        Pay now
                      </Link>
                    ) : (
                      <button
                        type="button"
                        onClick={() => downloadAgreementPdf(b.id)}
                        className="inline-flex items-center gap-1 text-xs link-underline"
                        data-testid={`download-pdf-${b.id}`}
                      >
                        <FileDown size={12} /> PDF
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


/**
 * LoyaltyCard — hero-style tier summary shown at the top of the customer
 * dashboard. Shows current tier + discount + a progress bar to the next
 * rung. Falls back to an "unlock your first tier" state when the customer
 * hasn't earned one yet.
 */
function LoyaltyCard({ loyalty }) {
  const current = loyalty.current_tier;
  const next = loyalty.next_tier;
  const completed = loyalty.completed_bookings || 0;
  const tiers = loyalty.tiers || [];
  const highestPct = tiers.reduce(
    (m, t) => Math.max(m, Number(t.discount_pct) || 0), 0,
  );

  const progressPct = (() => {
    if (!next) return 100;
    const min = Number(next.min_bookings) || 0;
    const prev = current ? Number(current.min_bookings) || 0 : 0;
    if (min <= prev) return 100;
    return Math.min(100, Math.max(0, ((completed - prev) / (min - prev)) * 100));
  })();

  return (
    <div
      className="mt-6 border border-black/10 bg-gradient-to-br from-[#F1ECDA] via-[#EFECE6] to-[#F8F5EC] p-6 md:p-8 rounded-lg"
      data-testid="loyalty-card"
    >
      <div className="flex flex-wrap items-start gap-4 justify-between">
        <div className="flex items-center gap-4">
          <div className={`w-14 h-14 rounded-full flex items-center justify-center ${current ? "bg-[#004B35] text-white" : "bg-neutral-200 text-neutral-500"}`}>
            <Award size={26} />
          </div>
          <div>
            <p className="overline">Car Rental App Rewards</p>
            {current ? (
              <>
                <h2 className="font-display text-3xl md:text-4xl mt-1" data-testid="loyalty-tier-name">
                  <em className="italic">{current.name}</em> member
                </h2>
                <p className="text-sm text-neutral-700 mt-1">
                  Enjoying <strong>{current.discount_pct}% off</strong> every rental · {completed} completed booking{completed === 1 ? "" : "s"}
                </p>
              </>
            ) : (
              <>
                <h2 className="font-display text-3xl md:text-4xl mt-1">
                  Unlock your <em className="italic">first tier</em>
                </h2>
                <p className="text-sm text-neutral-700 mt-1">
                  {completed} completed booking{completed === 1 ? "" : "s"} · save up to {highestPct}% at Platinum
                </p>
              </>
            )}
          </div>
        </div>
        {next && (
          <div className="text-right">
            <p className="overline">Next tier</p>
            <p className="font-display text-2xl mt-1">{next.name}</p>
            <p className="text-xs text-neutral-500 mt-1" data-testid="loyalty-bookings-to-next">
              {loyalty.bookings_to_next} more rental{loyalty.bookings_to_next === 1 ? "" : "s"} · {next.discount_pct}% off
            </p>
          </div>
        )}
      </div>

      {next && (
        <div className="mt-6">
          <div className="h-1.5 rounded-full bg-black/10 overflow-hidden" data-testid="loyalty-progress-bar">
            <div
              className="h-full bg-[#004B35] transition-all"
              style={{ width: `${progressPct}%` }}
            />
          </div>
          <div className="flex justify-between text-[10px] uppercase tracking-wider text-neutral-500 mt-2">
            <span>{current?.name || "Starter"}</span>
            <span>{next.name}</span>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * ReferralCard — share code + free-day credit tracker. Copy-to-clipboard
 * uses the Clipboard API (fallback: contentEditable trick). Both parties
 * earn one free-rental-day credit on the referred friend's first paid
 * booking; the credit ledger surfaces here so customers can see the
 * bonus grow.
 */
function ReferralCard({ referral }) {
  const shareUrl = `${window.location.origin}/register?ref=${referral.referral_code}`;
  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied to clipboard");
    } catch {
      toast.error("Copy failed — please copy manually");
    }
  };
  return (
    <div
      className="mt-6 border border-black/10 rounded-lg p-6 bg-white flex flex-col md:flex-row md:items-center gap-6"
      data-testid="referral-card"
    >
      <div className="flex-1">
        <p className="overline flex items-center gap-2"><Share2 size={13} /> Refer a friend</p>
        <p className="font-display text-2xl mt-2">
          You've earned <em className="italic">{referral.referral_credits}</em> free-day credit{referral.referral_credits === 1 ? "" : "s"}.
        </p>
        <p className="text-sm text-neutral-600 mt-2">
          Share your code — when a friend books their first rental, you BOTH get a free
          rental day added to your account.
        </p>
      </div>
      <div className="flex flex-col gap-2 md:min-w-[260px]">
        <div className="flex items-center gap-2">
          <code
            className="flex-1 font-mono text-xl text-center px-4 py-3 bg-black/[0.04] rounded tracking-widest"
            data-testid="referral-code"
          >
            {referral.referral_code}
          </code>
          <button
            type="button"
            onClick={() => copy(referral.referral_code)}
            className="p-3 border border-black/15 rounded hover:bg-black/5"
            data-testid="referral-copy-code"
            title="Copy code"
          >
            <Copy size={14} />
          </button>
        </div>
        <button
          type="button"
          onClick={() => copy(shareUrl)}
          className="text-xs px-3 py-2 border border-black/15 rounded hover:bg-black/5 inline-flex items-center justify-center gap-1"
          data-testid="referral-copy-link"
        >
          <Copy size={12} /> Copy share link
        </button>
      </div>
    </div>
  );
}
